const roundCurrency = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parseFloat(parsed.toFixed(2));
};

const normalizeTransactionType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'expense' ? 'expenses' : normalized;
};

const toFiniteSignedAmount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return roundCurrency(parsed);
};

const getVatSnapshotNetAmount = (transactionSnapshots = []) => roundCurrency(
  (Array.isArray(transactionSnapshots) ? transactionSnapshots : [])
    .reduce((sum, snapshot) => sum + (Number(snapshot?.signedVatAmount) || 0), 0)
);

const getFiledReturnCarryForwardDate = (period) => {
  const endDate = parseVatDateValue(period?.endDate);
  if (!endDate) return '';
  const carryDate = new Date(endDate);
  carryDate.setDate(carryDate.getDate() + 1);
  return formatVatDateForInput(carryDate);
};

const getNextDateAfterVatDate = (value) => {
  const parsedDate = parseVatDateValue(value);
  if (!parsedDate) return '';
  const nextDate = new Date(parsedDate);
  nextDate.setDate(nextDate.getDate() + 1);
  return formatVatDateForInput(nextDate);
};

const getTransactionAdjustmentCarryForwardDate = (snapshot, vatFiledPeriods = []) => {
  const sourcePeriodEndDate = formatVatDateForInput(snapshot?.periodEndDate);
  if (!sourcePeriodEndDate) return '';

  let carryAfterDate = sourcePeriodEndDate;
  const laterFiledPeriods = sanitizeVatFiledPeriods(vatFiledPeriods)
    .filter((period) => period.startDate > sourcePeriodEndDate)
    .sort((left, right) => left.startDate.localeCompare(right.startDate));

  laterFiledPeriods.forEach((period) => {
    const expectedNextStart = getNextDateAfterVatDate(carryAfterDate);
    if (period.startDate === expectedNextStart) {
      carryAfterDate = period.endDate;
    }
  });

  return getNextDateAfterVatDate(carryAfterDate);
};

const isVatCarryForwardAdjustmentSnapshotKey = (key) => (
  String(key || '').startsWith('filed-return:')
);

export const parseVatDateValue = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const timestampDate = value.toDate();
      if (timestampDate instanceof Date && !Number.isNaN(timestampDate.getTime())) {
        return timestampDate;
      }
    }

    const seconds = Number(value.seconds ?? value._seconds);
    const nanoseconds = Number(value.nanoseconds ?? value._nanoseconds);
    if (Number.isFinite(seconds)) {
      const millisFromSeconds = seconds * 1000 + (Number.isFinite(nanoseconds) ? Math.floor(nanoseconds / 1000000) : 0);
      const timestampDate = new Date(millisFromSeconds);
      if (!Number.isNaN(timestampDate.getTime())) {
        return timestampDate;
      }
    }

    const millis = Number(
      value.milliseconds ??
      value._milliseconds ??
      value.epochMillis ??
      value.epochMilliseconds
    );
    if (Number.isFinite(millis)) {
      const millisDate = new Date(millis);
      if (!Number.isNaN(millisDate.getTime())) {
        return millisDate;
      }
    }
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{10,13}$/.test(raw)) {
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) {
      const timestampDate = new Date(raw.length === 10 ? numeric * 1000 : numeric);
      if (!Number.isNaN(timestampDate.getTime())) {
        return timestampDate;
      }
    }
  }

  const directDate = new Date(raw);
  if (!Number.isNaN(directDate.getTime())) {
    return directDate;
  }

  const ymdMatch = raw.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
  if (ymdMatch) {
    const year = Number(ymdMatch[1]);
    const month = Number(ymdMatch[2]);
    const day = Number(ymdMatch[3]);
    const parsedYmd = new Date(year, month - 1, day);
    if (
      !Number.isNaN(parsedYmd.getTime()) &&
      parsedYmd.getFullYear() === year &&
      parsedYmd.getMonth() === month - 1 &&
      parsedYmd.getDate() === day
    ) {
      return parsedYmd;
    }
  }

  const dmyOrMdyMatch = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (dmyOrMdyMatch) {
    const first = Number(dmyOrMdyMatch[1]);
    const second = Number(dmyOrMdyMatch[2]);
    const rawYear = Number(dmyOrMdyMatch[3]);
    const year = rawYear < 100 ? (rawYear >= 70 ? rawYear + 1900 : rawYear + 2000) : rawYear;

    let day = first;
    let month = second;

    if (first <= 12 && second > 12) {
      day = second;
      month = first;
    } else if (first > 12 && second <= 12) {
      day = first;
      month = second;
    }

    const parsed = new Date(year, month - 1, day);
    if (
      !Number.isNaN(parsed.getTime()) &&
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day
    ) {
      return parsed;
    }
  }

  return null;
};

export const formatVatDateForInput = (dateValue) => {
  const parsedDate = parseVatDateValue(dateValue);
  if (!parsedDate) return '';
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeDateRangeStart = (value) => {
  const parsed = parseVatDateValue(value);
  if (!parsed) return null;
  const normalized = new Date(parsed);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const normalizeDateRangeEnd = (value) => {
  const parsed = parseVatDateValue(value);
  if (!parsed) return null;
  const normalized = new Date(parsed);
  normalized.setHours(23, 59, 59, 999);
  return normalized;
};

const isDateInRange = (value, start, end) => {
  const parsed = parseVatDateValue(value);
  if (!parsed || !start || !end) return false;
  return parsed >= start && parsed <= end;
};

const sortPeriodsBySnapshotRecency = (left, right) => {
  const leftTime =
    parseVatDateValue(left?.filedAt)?.getTime() ??
    parseVatDateValue(left?.endDate)?.getTime() ??
    parseVatDateValue(left?.startDate)?.getTime() ??
    0;
  const rightTime =
    parseVatDateValue(right?.filedAt)?.getTime() ??
    parseVatDateValue(right?.endDate)?.getTime() ??
    parseVatDateValue(right?.startDate)?.getTime() ??
    0;

  if (leftTime !== rightTime) return leftTime - rightTime;
  const leftId = String(left?.id || '');
  const rightId = String(right?.id || '');
  return leftId.localeCompare(rightId);
};

export const sanitizeVatFiledPeriods = (rawPeriods = []) => {
  if (!Array.isArray(rawPeriods)) return [];
  const seenIds = new Set();

  const sanitizedPeriods = rawPeriods
    .map((period) => {
      const startDate = formatVatDateForInput(period?.startDate);
      const endDate = formatVatDateForInput(period?.endDate);
      if (!startDate || !endDate || startDate > endDate) return null;

      const rawId = typeof period?.id === 'string' ? period.id.trim() : '';
      const id = rawId || `${startDate}__${endDate}`;
      if (seenIds.has(id)) return null;
      seenIds.add(id);

      const legacyTransactionKeys = Array.isArray(period?.transactionKeys)
        ? Array.from(
            new Set(
              period.transactionKeys
                .map((key) => String(key || '').trim())
                .filter(Boolean)
            )
          )
        : [];

      const transactionSnapshotMap = new Map();
      if (Array.isArray(period?.transactionSnapshots)) {
        period.transactionSnapshots.forEach((snapshot) => {
          const key = String(snapshot?.key ?? snapshot?.transactionKey ?? '').trim();
          if (!key) return;
          transactionSnapshotMap.set(key, {
            key,
            signedVatAmount: toFiniteSignedAmount(snapshot?.signedVatAmount)
          });
        });
      }

      const transactionKeys = Array.from(
        new Set([
          ...legacyTransactionKeys,
          ...Array.from(transactionSnapshotMap.keys())
        ])
      );

      const filedVatCollected = toFiniteSignedAmount(period?.filedVatCollected);
      const filedVatPaid = toFiniteSignedAmount(period?.filedVatPaid);
      const filedNetVat = toFiniteSignedAmount(period?.filedNetVat);
      const filedAmounts = {};
      if (filedVatCollected !== null) filedAmounts.filedVatCollected = filedVatCollected;
      if (filedVatPaid !== null) filedAmounts.filedVatPaid = filedVatPaid;
      if (filedNetVat !== null) filedAmounts.filedNetVat = filedNetVat;

      return {
        id,
        startDate,
        endDate,
        filedAt: typeof period?.filedAt === 'string' ? period.filedAt : '',
        transactionKeys,
        transactionSnapshots: Array.from(transactionSnapshotMap.values()),
        ...filedAmounts
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  const periodById = new Map(sanitizedPeriods.map((period) => [period.id, period]));

  return sanitizedPeriods.map((period) => {
    const transactionSnapshots = period.transactionSnapshots.map((snapshot) => {
      const sourcePeriodId = String(snapshot?.key || '').replace(/^filed-return:/, '');
      if (sourcePeriodId === snapshot?.key) return snapshot;

      const sourcePeriod = periodById.get(sourcePeriodId);
      if (!sourcePeriod || !hasVatFiledPeriodManualAmounts(sourcePeriod)) return snapshot;

      const calculatedNetAtFiling = getVatFiledPeriodSnapshotNetAmount(sourcePeriod);
      const actualFiledNet = getVatFiledPeriodNetAmount(sourcePeriod, calculatedNetAtFiling);
      return {
        ...snapshot,
        signedVatAmount: roundCurrency(calculatedNetAtFiling - actualFiledNet)
      };
    });

    return {
      ...period,
      transactionSnapshots
    };
  });
};

export const hasVatFiledPeriodManualAmounts = (period) => (
  toFiniteSignedAmount(period?.filedVatCollected) !== null ||
  toFiniteSignedAmount(period?.filedVatPaid) !== null ||
  toFiniteSignedAmount(period?.filedNetVat) !== null
);

export const getVatFiledPeriodSnapshotNetAmount = (period) =>
  getVatSnapshotNetAmount(period?.transactionSnapshots);

export const getVatFiledPeriodNetAmount = (period, fallbackNetAmount = 0) => {
  const explicitNet = toFiniteSignedAmount(period?.filedNetVat);
  if (explicitNet !== null) return explicitNet;

  const filedVatCollected = toFiniteSignedAmount(period?.filedVatCollected);
  const filedVatPaid = toFiniteSignedAmount(period?.filedVatPaid);
  if (filedVatCollected !== null || filedVatPaid !== null) {
    return roundCurrency((filedVatCollected || 0) - (filedVatPaid || 0));
  }

  return roundCurrency(fallbackNetAmount);
};

export const calculateVatFiledNetForTransactions = ({
  transactions = [],
  vatFiledPeriods = [],
  vatFiledSnapshotMap,
  getVatTransactionKey,
  getSignedTransactionVatAmount
}) => {
  if (!Array.isArray(transactions) || transactions.length === 0) return 0;
  if (typeof getVatTransactionKey !== 'function') return 0;

  const periods = sanitizeVatFiledPeriods(vatFiledPeriods);
  const snapshotMap = vatFiledSnapshotMap instanceof Map
    ? vatFiledSnapshotMap
    : buildLatestVatFiledSnapshotMap(periods);

  if (!snapshotMap.size && periods.length === 0) return 0;

  const selectedKeys = new Set();
  const transactionByKey = new Map();

  transactions.forEach((transaction) => {
    const key = getVatTransactionKey(transaction);
    if (!key || selectedKeys.has(key)) return;
    selectedKeys.add(key);
    transactionByKey.set(key, transaction);
  });

  if (!selectedKeys.size) return 0;

  let filedNet = 0;
  const periodsCoveredByActualFiledAmount = new Set();

  periods.forEach((period) => {
    if (!hasVatFiledPeriodManualAmounts(period)) return;

    const snapshotKeys = (Array.isArray(period.transactionSnapshots) ? period.transactionSnapshots : [])
      .map((snapshot) => String(snapshot?.key || '').trim())
      .filter((key) => key && !isVatCarryForwardAdjustmentSnapshotKey(key));
    if (!snapshotKeys.length) return;

    const isFullPeriodSelected = snapshotKeys.every((key) => selectedKeys.has(key));
    if (!isFullPeriodSelected) return;

    const snapshotNetAmount = getVatFiledPeriodSnapshotNetAmount(period);
    filedNet += getVatFiledPeriodNetAmount(period, snapshotNetAmount);
    periodsCoveredByActualFiledAmount.add(period.id);
  });

  selectedKeys.forEach((key) => {
    const snapshot = snapshotMap.get(key);
    if (!snapshot) return;
    if (periodsCoveredByActualFiledAmount.has(snapshot.periodId)) return;

    const transaction = transactionByKey.get(key);
    const fallbackSignedVatAmount = typeof getSignedTransactionVatAmount === 'function'
      ? getSignedTransactionVatAmount(transaction)
      : 0;
    filedNet += Number(resolveVatFiledBaselineAmount(snapshot, fallbackSignedVatAmount) || 0);
  });

  return roundCurrency(filedNet);
};

export const buildLatestVatFiledSnapshotMap = (vatFiledPeriods = []) => {
  const periods = sanitizeVatFiledPeriods(vatFiledPeriods)
    .slice()
    .sort(sortPeriodsBySnapshotRecency);
  const snapshotMap = new Map();

  periods.forEach((period) => {
    const explicitSnapshotKeys = new Set();

    (period.transactionSnapshots || []).forEach((snapshot) => {
      explicitSnapshotKeys.add(snapshot.key);
      snapshotMap.set(snapshot.key, {
        key: snapshot.key,
        signedVatAmount: toFiniteSignedAmount(snapshot.signedVatAmount),
        filedAt: period.filedAt || '',
        periodId: period.id,
        periodStartDate: period.startDate,
        periodEndDate: period.endDate,
        isLegacySnapshot: false
      });
    });

    (period.transactionKeys || []).forEach((key) => {
      if (!key || explicitSnapshotKeys.has(key)) return;
      snapshotMap.set(key, {
        key,
        signedVatAmount: null,
        filedAt: period.filedAt || '',
        periodId: period.id,
        periodStartDate: period.startDate,
        periodEndDate: period.endDate,
        isLegacySnapshot: true
      });
    });
  });

  return snapshotMap;
};

export const resolveVatFiledBaselineAmount = (snapshot, fallbackCurrentSignedVatAmount = 0) => {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const explicitAmount = toFiniteSignedAmount(snapshot.signedVatAmount);
  if (explicitAmount !== null) return explicitAmount;
  return toFiniteSignedAmount(fallbackCurrentSignedVatAmount);
};

export const getVatCarryForwardAdjustmentSnapshotAmount = (adjustment) => {
  const deltaAmount = toFiniteSignedAmount(adjustment?.deltaSignedVatAmount);
  if (deltaAmount !== null) return deltaAmount;
  return toFiniteSignedAmount(adjustment?.currentSignedVatAmount) || 0;
};

export const buildVatCarryForwardAdjustments = ({
  transactions = [],
  startDate,
  endDate,
  vatFiledPeriods = [],
  vatFiledSnapshotMap,
  getVatTransactionKey,
  getSignedTransactionVatAmount,
  getTransactionDateValue,
  getTransactionUpdatedDateValue
}) => {
  if (!Array.isArray(transactions) || !(vatFiledSnapshotMap instanceof Map)) return [];
  if (typeof getVatTransactionKey !== 'function' ||
      typeof getSignedTransactionVatAmount !== 'function' ||
      typeof getTransactionDateValue !== 'function' ||
      typeof getTransactionUpdatedDateValue !== 'function') {
    return [];
  }

  const rangeStart = normalizeDateRangeStart(startDate);
  const rangeEnd = normalizeDateRangeEnd(endDate);
  if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return [];

  const seenKeys = new Set();
  const adjustments = [];

  transactions.forEach((transaction) => {
    const key = getVatTransactionKey(transaction);
    if (!key || seenKeys.has(key)) return;
    seenKeys.add(key);

    const snapshot = vatFiledSnapshotMap.get(key);
    const previousSignedVatAmount = toFiniteSignedAmount(snapshot?.signedVatAmount);
    if (!snapshot || previousSignedVatAmount === null) return;

    const originalTransactionDate = getTransactionDateValue(transaction);
    if (isDateInRange(originalTransactionDate, rangeStart, rangeEnd)) return;

    const adjustmentDate = getTransactionUpdatedDateValue(transaction);

    const filedAtDate = parseVatDateValue(snapshot.filedAt);
    if (filedAtDate && adjustmentDate <= filedAtDate) return;

    const currentSignedVatAmount = roundCurrency(getSignedTransactionVatAmount(transaction));
    const deltaSignedVatAmount = roundCurrency(currentSignedVatAmount - previousSignedVatAmount);
    if (deltaSignedVatAmount === 0) return;

    const carryForwardDate = getTransactionAdjustmentCarryForwardDate(snapshot, vatFiledPeriods);
    if (!isDateInRange(carryForwardDate, rangeStart, rangeEnd)) return;

    adjustments.push({
      key,
      type: normalizeTransactionType(transaction?.type),
      category: String(transaction?.category || ''),
      description: String(transaction?.description || ''),
      originalDate: formatVatDateForInput(originalTransactionDate),
      adjustmentDate: carryForwardDate,
      editedDate: formatVatDateForInput(adjustmentDate),
      previousSignedVatAmount,
      currentSignedVatAmount,
      deltaSignedVatAmount,
      previousVatAmount: roundCurrency(Math.abs(previousSignedVatAmount)),
      currentVatAmount: roundCurrency(Math.abs(currentSignedVatAmount)),
      deltaVatAmount: roundCurrency(Math.abs(deltaSignedVatAmount))
    });
  });

  return adjustments.sort((left, right) => {
    const leftTime = parseVatDateValue(left.adjustmentDate)?.getTime() ?? 0;
    const rightTime = parseVatDateValue(right.adjustmentDate)?.getTime() ?? 0;
    if (leftTime !== rightTime) return leftTime - rightTime;
    return left.key.localeCompare(right.key);
  });
};

export const buildVatFiledReturnAdjustments = ({
  vatFiledPeriods = [],
  startDate,
  endDate
}) => {
  const rangeStart = normalizeDateRangeStart(startDate);
  const rangeEnd = normalizeDateRangeEnd(endDate);
  if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return [];

  return sanitizeVatFiledPeriods(vatFiledPeriods)
    .map((period) => {
      if (!hasVatFiledPeriodManualAmounts(period)) return null;

      const adjustmentDate = getFiledReturnCarryForwardDate(period);
      if (!isDateInRange(adjustmentDate, rangeStart, rangeEnd)) return null;

      const calculatedNetAtFiling = getVatFiledPeriodSnapshotNetAmount(period);
      const actualFiledNet = getVatFiledPeriodNetAmount(period, calculatedNetAtFiling);
      const deltaSignedVatAmount = roundCurrency(calculatedNetAtFiling - actualFiledNet);
      if (deltaSignedVatAmount === 0) return null;

      return {
        key: `filed-return:${period.id}`,
        source: 'filed_return_variance',
        type: 'vat_adjustment',
        category: 'filed_return',
        description: `Filed return variance for ${period.startDate} to ${period.endDate}`,
        originalDate: period.endDate,
        adjustmentDate,
        previousSignedVatAmount: actualFiledNet,
        currentSignedVatAmount: calculatedNetAtFiling,
        deltaSignedVatAmount,
        previousVatAmount: roundCurrency(Math.abs(actualFiledNet)),
        currentVatAmount: roundCurrency(Math.abs(calculatedNetAtFiling)),
        deltaVatAmount: roundCurrency(Math.abs(deltaSignedVatAmount))
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const leftTime = parseVatDateValue(left.adjustmentDate)?.getTime() ?? 0;
      const rightTime = parseVatDateValue(right.adjustmentDate)?.getTime() ?? 0;
      if (leftTime !== rightTime) return leftTime - rightTime;
      return left.key.localeCompare(right.key);
    });
};

export const summarizeVatCarryForwardAdjustments = (adjustments = []) => {
  if (!Array.isArray(adjustments) || adjustments.length === 0) {
    return {
      count: 0,
      netAdjustment: 0,
      vatDue: 0,
      vatReclaim: 0
    };
  }

  const netAdjustment = roundCurrency(
    adjustments.reduce((sum, adjustment) => sum + (Number(adjustment?.deltaSignedVatAmount) || 0), 0)
  );

  return {
    count: adjustments.length,
    netAdjustment,
    vatDue: roundCurrency(Math.max(0, netAdjustment)),
    vatReclaim: roundCurrency(Math.max(0, -netAdjustment))
  };
};
